// Domains API endpoints
// Covers: CRUD, purchase, renewal, search, pricing, BYOK registrar keys,
// registration history, and Cloudflare setup status.

import { api } from './client'

// =============================================================================
// Types
// =============================================================================

export interface Domain {
  id: string
  domain: string
  type: DomainType
  status: DomainStatus
  sslStatus: SslStatus
  isDefault: boolean
  cloudflareZoneId?: string
  dnsRecords?: DnsRecord[]
  createdAt: string
  updatedAt: string
  verifiedAt?: string
  /** Set when domain was purchased through GhostFlow */
  expiresAt?: string | null
  isRegistered?: boolean
  cfSetupStatus?: CfSetupStatus | null
  autoRenew?: boolean
  /** SSL diagnostic message for PendingSsl/SslFailed domains */
  sslDiagnostic?: string
  /** Required actions user must take to fix SSL */
  requiredActions?: string[]
}

export type DomainType = 'tracking' | 'landing' | 'both'
export type DomainStatus = 'pending' | 'pending_ssl' | 'active' | 'error' | 'suspended'
export type SslStatus = 'pending' | 'active' | 'expired' | 'error'

/** Cloudflare for SaaS setup status for purchased domains */
export type CfSetupStatus =
  | 'pending'
  | 'hostname_created'
  | 'ns_changed'
  | 'ssl_active'
  | 'failed'

export interface DnsRecord {
  type: 'A' | 'AAAA' | 'CNAME' | 'TXT' | 'MX'
  name: string
  content: string
  ttl: number
  proxied?: boolean
}

export interface CreateDomainRequest {
  domain: string
  type: DomainType
}

export interface UpdateDomainRequest {
  type?: DomainType
  isDefault?: boolean
}

export interface DomainVerification {
  status: 'pending' | 'verified' | 'failed'
  records: {
    type: string
    name: string
    expected: string
    actual?: string
    verified: boolean
  }[]
  /** Whether ALL required DNS records are correctly configured (from actual DNS lookups) */
  dnsVerified: boolean
  /** Current domain lifecycle status */
  domainStatus?: string
  /** SSL diagnostic info (populated when domain is PendingSsl/SslFailed) */
  sslDiagnostic?: string
  /** Actions the user should take to fix SSL */
  requiredActions?: string[]
}

export interface DomainSearchResult {
  name: string
  available: boolean
  premium: boolean
  /** NameSilo base price in USD */
  priceUsd: number
  /** Total price with agency markup in USD cents */
  priceWithMarkupCents: number
  /** Agency markup in cents (0 for BYOK users) */
  markupCents: number
  currency: string
}

export interface PurchaseDomainRequest {
  domain: string
  type?: DomainType
  autoRenew?: boolean
  /** Optional purchase mode: "agency" (default) or "byok" */
  mode?: 'agency' | 'byok'
}

/** Matches backend `PurchaseResponse` (camelCase) */
export interface PurchaseDomainResponse {
  registrationId: string
  domain: string
  status: string
  expiresAt?: string
  autoRenew: boolean
  whoisPrivacy: boolean
  nameserversUpdated: boolean
  amountChargedCents: number
  balanceAfterCents?: number
  provider: string
  warnings: string[]
}

/** Matches backend `RenewDomainDto` */
export interface RenewDomainRequest {
  years?: number
}

/** Matches backend `RenewalResponse` (camelCase) */
export interface RenewDomainResponse {
  registrationId: string
  domain: string
  newExpiresAt?: string
  amountChargedCents: number
  balanceAfterCents?: number
  provider: string
}

/** Single TLD pricing row from GET /domains/pricing */
export interface TldPricing {
  tld: string
  registrationPriceCents: number
  renewalPriceCents: number
  markupCents: number
}

/** GET /domains/pricing response wrapper */
export interface DomainPricingResponse {
  tlds: TldPricing[]
}

/** Matches backend `RegistrationRecordResponse` (camelCase) */
export interface DomainRegistrationRecord {
  id: string
  domain: string
  operation: string
  provider: string
  providerOrderId?: string
  amountCents: number
  markupCents: number
  status: string
  years: number
  registeredAt?: string
  expiresAt?: string
  autoRenew: boolean
  whoisPrivacy: boolean
  nameserversChanged: boolean
  errorMessage?: string
  createdAt: string
}

/** Matches backend `RegistrarKeyResponse` (camelCase) */
export interface UserRegistrarKey {
  id: string
  provider: string
  label: string
  isActive: boolean
  maskedKey: string
  domainsCount: number
  lastVerifiedAt?: string
  createdAt: string
}

export interface SaveRegistrarKeyRequest {
  provider: string
  apiKey: string
  label?: string
}

export interface VerifyRegistrarKeyResponse {
  valid: boolean
  balance?: string
  error?: string
}

/** Matches backend `DomainRegistrationInfoResponse` */
export interface DomainRegistrationInfo {
  domain: string
  registrar: string
  status: string
  expires?: string
  nameservers: string[]
  privacy: boolean
  autoRenew: boolean
  locked: boolean
}

/** Matches backend `ToggleAutoRenewResponse` */
export interface ToggleAutoRenewResponse {
  domain: string
  autoRenew: boolean
  message: string
}

/** Matches backend `DnsInstructionsResponse` (camelCase) */
export interface DnsInstructionsResponse {
  records: DnsRecord[]
  instructions: string
  domainType: DomainType
  recommendedSubdomain: string
  setupSteps: DnsSetupStep[]
  warnings: string[]
}

/** Step-by-step DNS setup instructions */
export interface DnsSetupStep {
  step: number
  title: string
  description: string
  example?: string
}

/** Matches backend `ValidateDomainTypeResponse` (camelCase) */
export interface ValidateDomainTypeResponse {
  valid: boolean
  domain: string
  actualType: string
  expectedType: string
  warning?: string
}

// =============================================================================
// API Client
// =============================================================================

// Base path for domain endpoints
const BASE_PATH = '/v1/domains'

export const domainApi = {
  // -------------------------------------------------------------------------
  // Domain CRUD
  // -------------------------------------------------------------------------

  /** List domains with optional filters */
  list: async (params?: { type?: DomainType; status?: DomainStatus }): Promise<Domain[]> => {
    const queryParams = new URLSearchParams()
    if (params?.type) queryParams.set('type', params.type)
    if (params?.status) queryParams.set('status', params.status)
    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return api.get<Domain[]>(`${BASE_PATH}${query}`)
  },

  /** Get single domain by ID */
  get: async (id: string): Promise<Domain> => {
    return api.get<Domain>(`${BASE_PATH}/${id}`)
  },

  /** Add an external domain (not purchased through GhostFlow) */
  create: async (data: CreateDomainRequest): Promise<Domain> => {
    return api.post<Domain>(BASE_PATH, data)
  },

  /** Update domain type or default flag */
  update: async (id: string, data: UpdateDomainRequest): Promise<Domain> => {
    return api.patch<Domain>(`${BASE_PATH}/${id}`, data)
  },

  /** Delete domain */
  delete: async (id: string): Promise<void> => {
    return api.delete(`${BASE_PATH}/${id}`)
  },

  // -------------------------------------------------------------------------
  // Verification & DNS
  // -------------------------------------------------------------------------

  /** Trigger DNS verification */
  verify: async (id: string): Promise<DomainVerification> => {
    return api.post<DomainVerification>(`${BASE_PATH}/${id}/verify`)
  },

  /** Get verification status */
  getVerificationStatus: async (id: string): Promise<DomainVerification> => {
    return api.get<DomainVerification>(`${BASE_PATH}/${id}/verification`)
  },

  /** Get DNS setup instructions (includes domain-type-specific guidance) */
  getDnsInstructions: async (id: string): Promise<DnsInstructionsResponse> => {
    return api.get(`${BASE_PATH}/${id}/dns-instructions`)
  },

  /** Refresh SSL certificate */
  refreshSsl: async (id: string): Promise<Domain> => {
    return api.post<Domain>(`${BASE_PATH}/${id}/refresh-ssl`)
  },

  /** Set as default domain */
  setDefault: async (id: string): Promise<Domain> => {
    return api.post<Domain>(`${BASE_PATH}/${id}/set-default`)
  },

  // -------------------------------------------------------------------------
  // Availability & Search
  // -------------------------------------------------------------------------

  /** Check single domain availability (quick check) */
  checkAvailability: async (domain: string): Promise<{
    available: boolean
    reason?: string
  }> => {
    return api.get(`${BASE_PATH}/check?domain=${encodeURIComponent(domain)}`)
  },

  /** Validate that a domain matches the expected type (e.g. tracking domain for campaign) */
  validateType: async (
    domain: string,
    expectedType: DomainType,
  ): Promise<ValidateDomainTypeResponse> => {
    const params = new URLSearchParams({ domain, expectedType })
    return api.get<ValidateDomainTypeResponse>(
      `${BASE_PATH}/validate-type?${params.toString()}`,
    )
  },

  /** Search domains for purchase across TLDs (includes pricing if backend supports) */
  searchForPurchase: async (
    query: string,
    includePricing = true,
  ): Promise<DomainSearchResult[]> => {
    const params = new URLSearchParams({ query, includePricing: String(includePricing) })
    return api.get<DomainSearchResult[]>(`${BASE_PATH}/search?${params.toString()}`)
  },

  // -------------------------------------------------------------------------
  // Purchase & Renewal
  // -------------------------------------------------------------------------

  /** Purchase a domain via NameSilo (through agency or BYOK) */
  purchase: async (data: PurchaseDomainRequest): Promise<PurchaseDomainResponse> => {
    return api.post<PurchaseDomainResponse>(`${BASE_PATH}/purchase`, data)
  },

  /** Renew a purchased domain */
  renewDomain: async (id: string, data?: RenewDomainRequest): Promise<RenewDomainResponse> => {
    return api.post<RenewDomainResponse>(`${BASE_PATH}/${id}/renew`, data ?? {})
  },

  /** Toggle auto-renewal for a purchased domain */
  toggleAutoRenew: async (
    id: string,
    autoRenew: boolean,
  ): Promise<ToggleAutoRenewResponse> => {
    return api.put<ToggleAutoRenewResponse>(`${BASE_PATH}/${id}/auto-renew`, {
      autoRenew,
    })
  },

  // -------------------------------------------------------------------------
  // Pricing
  // -------------------------------------------------------------------------

  /** Get TLD pricing table */
  getPricing: async (): Promise<DomainPricingResponse> => {
    return api.get<DomainPricingResponse>(`${BASE_PATH}/pricing`)
  },

  // -------------------------------------------------------------------------
  // Registration History
  // -------------------------------------------------------------------------

  /** List domain registration / renewal records */
  getRegistrations: async (
    limit = 20,
    offset = 0,
  ): Promise<DomainRegistrationRecord[]> => {
    return api.get<DomainRegistrationRecord[]>(
      `${BASE_PATH}/registrations?limit=${limit}&offset=${offset}`,
    )
  },

  /** Get NameSilo registration info for a specific domain */
  getRegistrationInfo: async (id: string): Promise<DomainRegistrationInfo> => {
    return api.get<DomainRegistrationInfo>(`${BASE_PATH}/${id}/registration-info`)
  },

  // -------------------------------------------------------------------------
  // BYOK Registrar Keys
  // -------------------------------------------------------------------------

  /** Save (create/update) a BYOK registrar API key */
  saveRegistrarKey: async (data: SaveRegistrarKeyRequest): Promise<UserRegistrarKey> => {
    return api.post<UserRegistrarKey>(`${BASE_PATH}/registrar-keys`, data)
  },

  /** List all saved BYOK registrar keys */
  listRegistrarKeys: async (): Promise<UserRegistrarKey[]> => {
    return api.get<UserRegistrarKey[]>(`${BASE_PATH}/registrar-keys`)
  },

  /** Delete a BYOK registrar key */
  deleteRegistrarKey: async (id: string): Promise<void> => {
    return api.delete(`${BASE_PATH}/registrar-keys/${id}`)
  },

  /** Re-verify a BYOK registrar key */
  verifyRegistrarKey: async (id: string): Promise<VerifyRegistrarKeyResponse> => {
    return api.post<VerifyRegistrarKeyResponse>(
      `${BASE_PATH}/registrar-keys/${id}/verify`,
    )
  },
}
